import {queryOptions, useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {UserListsPayload} from '../types'

/** Returns the query key used to cache user star lists for a given API path. */
export function userListsQueryKey(listsApiPath: string) {
  return ['repo-star-lists', listsApiPath] as const
}

function userListsQueryOptions(listsApiPath: string) {
  return queryOptions({
    queryKey: userListsQueryKey(listsApiPath),
    queryFn: async () => {
      const fetchPath = new URL(listsApiPath, window.location.origin)
      fetchPath.searchParams.set('reloadAt', String(Date.now()))

      const response = await verifiedFetchJSON(`${fetchPath.pathname}${fetchPath.search}`, {cache: 'no-store'})

      if (!response.ok) {
        throw new Error('Failed to load lists')
      }

      return (await response.json()) as UserListsPayload
    },
  })
}

interface UseUserListsQueryOptions {
  /** API path used to fetch user lists */
  listsApiPath: string
  /** Whether to enable the query (typically tied to dropdown open state) */
  enabled?: boolean
}

/**
 * Hook to lazily fetch user star lists for a repository.
 * Uses React Query to manage caching, loading, and error state.
 */
export function useUserListsQuery({listsApiPath, enabled}: UseUserListsQueryOptions) {
  const {data, isLoading, isError, isFetching} = useQuery({
    ...userListsQueryOptions(listsApiPath),
    enabled,
  })

  return {
    listsData: data ?? null,
    isLoading,
    isError,
    isFetching,
  }
}
